import { useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { withoutBidiControls as plain } from "../snippets/bidi";
import MoneyMoved from "./MoneyMoved";
import TargetBreaches from "./TargetBreaches";
import { formatPct } from "./lib/money";
import { outcomeUnknown } from "./lib/outcome";
import { revealLater } from "./lib/reveal";
import useSubmitGuard from "../../hooks/useSubmitGuard";

const MAIN = "h-11 md:h-9";
const REASON_MAX = 1000; // backend/investing/constants.py
const SYNC_TASK_TITLE = "Investing: sync to target v{number}"; // targets.SYNC_TASK_TITLE

/** What approving does, and for whom (D3, D12): the sync task goes to the executor. */
function approveText(version, current, executor) {
  const replaces = current ? ` and v${current.number} is kept as history` : "";
  const task = SYNC_TASK_TITLE.replace("{number}", version.number);
  const who = executor?.valid && executor.username ? plain(executor.username) : "your executor";
  return (
    `v${version.number} becomes the current target${replaces}. Approving creates the board task “${task}” ` +
    `for ${who}, who syncs the account to it — Robinhood then asks you to approve each order on your phone.`
  );
}

function declineText(version, current) {
  const stays = current
    ? `the current target (v${current.number}) stays as it is`
    : "no target is approved until another change is";
  return `v${version.number} is kept in the history as declined, and ${stays}. Nothing trades.`;
}

/**
 * Approve or decline a proposed target change as a whole (walkthrough 8,
 * spec §2.1: a person's, in their own browser). The confirm STATES the new
 * target — every holding and cash, what it sells off, its cap breaches
 * with their overrides, the money it moves at the latest snapshot — and
 * sends the `revision` of the version it shows (`version` is held from the
 * moment it opened). A 409 (a stale revision, a moved target, no executor
 * that can act) is said in the server's words, with a reload. No answer
 * or a 5xx leaves the outcome unknown: `onUnknown` closes it and the page
 * says so and reads the target again — never "Couldn't approve it".
 *
 * `onDecided(version)` gets the version as the API returned it (approved:
 * with its `sync_task`). Static backdrop; Escape and Cancel are refused
 * while it runs.
 */
function DecideTargetDialog({
  open,
  decision,
  version,
  current,
  latest,
  executor,
  onOpenChange,
  onDecided,
  onReload,
  onUnknown,
  onCloseAutoFocus,
}) {
  const id = useId();
  const approving = decision === "approved";
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null); // {message, conflict}
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const errorRef = useRef(null);
  const submitGuard = useSubmitGuard(open);

  // Each opening starts empty — set while rendering the opening, not in an
  // effect (Radix focuses in its own mount effect, which runs first).
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason("");
      setError(null);
      setBusy(false);
    }
  }

  const decide = async (event) => {
    event.preventDefault();
    // No busy check: the submit button is disabled while it runs.
    setBusy(true);
    setError(null);
    try {
      const { data } = await investingApi.target.versions.decide(version.number, {
        revision: version.revision,
        decision,
        reason: reason.trim(),
      });
      setBusy(false);
      onDecided(data);
    } catch (err) {
      setBusy(false);
      // No answer, or the server failed partway: it may have landed.
      if (outcomeUnknown(err)) {
        onUnknown({ number: version.number, decision, message: apiErrorMessage(err, "No answer came back.") });
        return;
      }
      const conflict = err?.response?.status === 409;
      const message = apiErrorMessage(err, approving ? "Couldn't approve it." : "Couldn't decline it.");
      setError({ message: conflict ? `Nothing changed: ${message}` : message, conflict });
      revealLater(errorRef);
    }
  };

  // After a 409 the page is stale, however the confirm closes: read it again.
  const close = () => (error?.conflict ? onReload() : onOpenChange(false));

  const held = (version.holdings || []).filter((holding) => Number(holding.weight) > 0);
  const changes = Array.isArray(version.diff?.holdings) ? version.diff.holdings : [];
  const soldOff = changes.filter((change) => change.change === "removed");
  const breaches = Array.isArray(version.cap_breaches) ? version.cap_breaches : [];
  const verb = approving ? "Approve" : "Decline";
  const listLabel = approving ? "The new target" : "The target you're declining";

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : close())}>
      <DialogContent
        data-investing=""
        showCloseButton={false}
        className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          // The statement first: focus its top, not the reason at its end
          // (on a phone that would scroll the target out of view).
          event.preventDefault();
          bodyRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="font-display text-xl">
            {approving ? `Approve v${version.number} as the target?` : `Decline v${version.number}?`}
          </DialogTitle>
          <DialogDescription>
            {approving ? approveText(version, current, executor) : declineText(version, current)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={decide} noValidate className="flex min-h-0 flex-1 flex-col">
          <div
            ref={bodyRef}
            tabIndex={-1}
            data-slot="dialog-body"
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4 outline-none"
          >
            {approving && executor && !executor.valid && (
              <p className="m-0 rounded-md border border-destructive/60 bg-destructive/5 p-3 text-sm">
                No executor can act now, so approving will be refused: approval creates the sync task for your
                executor. Name one in{" "}
                <Link to="/investing/settings" className="underline underline-offset-2">
                  Settings
                </Link>{" "}
                first.
              </p>
            )}
            <div>
              <h3 className="m-0 text-sm font-semibold">{listLabel}</h3>
              <ul aria-label={listLabel} className="m-0 flex list-none flex-col p-0">
                {held.map((holding) => (
                  <li key={holding.symbol} className="flex items-baseline gap-2 border-b border-border py-1 text-sm">
                    <span className="min-w-0 flex-1 wrap-anywhere">
                      <span className="font-semibold">{holding.symbol}</span> — {plain(holding.name)}
                    </span>{" "}
                    <span className="font-semibold tabular-nums">{formatPct(holding.weight)}</span>
                  </li>
                ))}
                <li className="flex items-baseline gap-2 py-1 text-sm">
                  <span className="flex-1 font-semibold">Cash</span>{" "}
                  <span className="font-semibold tabular-nums">{formatPct(version.cash_weight)}</span>
                </li>
              </ul>
              {soldOff.length > 0 && (
                <p className="mt-1 mb-0 text-sm">
                  Sold off:{" "}
                  {soldOff.map((change) => `${change.symbol} (${formatPct(change.weight_before)} → 0%)`).join(", ")}
                </p>
              )}
            </div>
            <div>
              <h3 className="m-0 mb-1 text-sm font-semibold">Cap breaches</h3>
              {breaches.length > 0 ? (
                <TargetBreaches breaches={breaches} version={version} />
              ) : (
                <p className="m-0 text-sm">None — every cap holds.</p>
              )}
            </div>
            <div>
              <h3 className="m-0 mb-1 text-sm font-semibold">The money it moves</h3>
              <MoneyMoved changes={changes} latest={latest} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-reason`}>Reason (optional)</Label>
              <Textarea
                id={`${id}-reason`}
                value={reason}
                maxLength={REASON_MAX}
                placeholder={approving ? "e.g. Agree — AMD's share gains are real" : "e.g. Too much in one sector"}
                className="max-h-48 min-h-16"
                disabled={busy}
                aria-describedby={`${id}-reason-help`}
                onChange={(event) => setReason(event.target.value)}
              />
              <p id={`${id}-reason-help`} className="m-0 text-xs text-muted-foreground">
                Kept with the decision in the version&apos;s history.
              </p>
            </div>
            {error && (
              <Alert ref={errorRef} variant="destructive" role="alert" tabIndex={-1} className="outline-none">
                <AlertDescription className="flex flex-col items-start gap-2">
                  <span>{error.message}</span>
                  {error.conflict && (
                    <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={onReload}>
                      Reload the target
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" className={MAIN} disabled={busy} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" className={MAIN} disabled={busy} {...submitGuard}>
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy ? `${approving ? "Approving" : "Declining"}…` : `${verb} v${version.number}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

DecideTargetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  decision: PropTypes.oneOf(["approved", "declined"]).isRequired,
  /** The proposed version as the person saw it when the confirm opened. */
  version: PropTypes.shape({
    number: PropTypes.number.isRequired,
    revision: PropTypes.number.isRequired,
    cash_weight: PropTypes.string.isRequired,
    holdings: PropTypes.arrayOf(PropTypes.object),
    cap_breaches: PropTypes.arrayOf(PropTypes.object),
    diff: PropTypes.shape({ holdings: PropTypes.arrayOf(PropTypes.object) }),
  }).isRequired,
  /** The current target it replaces; null before the first. */
  current: PropTypes.shape({ number: PropTypes.number.isRequired }),
  /** useLatestSnapshot's state: the money it moves. */
  latest: PropTypes.object.isRequired,
  /** The executor the sync task goes to (Settings), when known. */
  executor: PropTypes.shape({ username: PropTypes.string, valid: PropTypes.bool.isRequired }),
  onOpenChange: PropTypes.func.isRequired,
  /** `(version)`: decided; the version as the API returned it. */
  onDecided: PropTypes.func.isRequired,
  /** A 409's reload: close, and load the target again — on any close after a 409. */
  onReload: PropTypes.func.isRequired,
  /** `({number, decision, message})`: no answer or a 5xx — it may or may not have landed. */
  onUnknown: PropTypes.func.isRequired,
  /** Radix's close autofocus: the page moves focus when the opener goes away. */
  onCloseAutoFocus: PropTypes.func,
};

export default DecideTargetDialog;
